import { startTransition, useEffect, useEffectEvent, useMemo, useState } from "react";
import { Button, Col, Form, Modal, Nav, Row, Tab } from "react-bootstrap";
import { PlusCircle } from "react-bootstrap-icons";
import { AsyncButton, ReorderableNavList } from "../../controls";
import { getFromDataPath } from "../../utility";
import {
  Group,
  GroupingFactorDataDriven,
  GroupingFactorWithGroups,
  GroupWithCondition,
} from "../ars_ldm";
import { createNewGroup, useArsContext } from "../contexts";
import { PATCH_ARS_STATE } from "../contexts/interface";
import { ArsText } from "../inputs/ArsText";
import { ArsVariable } from "../inputs/ArsVariable";
import { isGroupingFactor } from "../typeGuards/isGroupingFactor";
import { isGroupingFactorDataDriven } from "../typeGuards/isGroupingFactorDataDriven";
import { isGroupingFactorWithGroups } from "../typeGuards/isGroupingFactorWithGroups";
import { GroupControl } from "./GroupControl";
import { NamedObjectControl } from "./NamedObjectControl";
import { RemoveArsItemButton } from "./RemoveArsItemButton";

/**
 * Props for the GroupingFactorControl component.
 */
export interface GroupingFactorControlProps {
  /** RFC 6901 JSON Pointer path to the GroupingFactor in ArsContext state */
  dataPath: string;
  /** Can rename the object */
  canRename?: boolean;
  /** Disabled state */
  disabled?: boolean;
}

/**
 * A component for managing GroupingFactor entities with full CRUD capabilities.
 *
 * This component provides:
 * - NamedObjectControl for editing name, description, and label
 * - ArsVariable for selecting grouping dataset and variable
 * - Checkbox for the dataDriven boolean field
 * - Management of nested Group entities using GroupControl
 * - RemoveButton for deleting the grouping factor
 *
 * When dataDriven is true, the groups section is disabled as groups are
 * determined automatically from data values.
 *
 * @param props - Component props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the GroupingFactor in ArsContext state.
 * @param props.canRename - Optional flag to allow renaming the object.
 * @param props.disabled - Optional flag to disable editing controls.
 * @returns A React functional component rendering the grouping factor editor.
 */
export const GroupingFactorControl = ({
  dataPath,
  canRename,
  disabled = false,
}: GroupingFactorControlProps): React.ReactElement => {
  const { state, dispatch } = useArsContext();
  const [activeTab, setActiveTab] = useState<string>("named-object");
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Get the current grouping factor from state
  const groupingFactor = getFromDataPath(dataPath, state, isGroupingFactor);
  const dataDriven = isGroupingFactorDataDriven(groupingFactor);
  const groups: Group[] = useMemo(
    () => (isGroupingFactorWithGroups(groupingFactor) ? groupingFactor.groups : []),
    [groupingFactor],
  );
  // Disable editing for parent templates
  if (groupingFactor?.disableEditing) disabled = true;

  /**
   * Event handler to update the active tab.
   */
  const updateActiveTab = useEffectEvent((newTab: string) => {
    startTransition(() => setActiveTab(newTab));
  });

  /**
   * Change the active group tab if it no longer exists
   */
  useEffect(() => {
    if (
      activeTab.startsWith("group-") &&
      groups.length > 0 &&
      parseInt(activeTab.split("-")[1], 10) >= groups.length
    ) {
      const lastGroupIndex = groups.length - 1;
      updateActiveTab(`group-${lastGroupIndex}`);
    } else if (activeTab.startsWith("group-") && groups.length === 0) {
      updateActiveTab("named-object");
    }
  }, [groups.length, dataDriven, activeTab]);

  /**
   * Handles showing the confirmation modal when toggling dataDriven.
   */
  const handleDataDrivenChange = (): void => {
    // istanbul ignore next
    if (disabled) return;
    if (
      dataDriven &&
      groupingFactor.groupingDataset === "" &&
      groupingFactor.groupingVariable === "" &&
      groupingFactor.dataValues.length === 0
    ) {
      confirmDataDrivenChange();
    } else if (!dataDriven && groups.length === 0) {
      confirmDataDrivenChange();
    } else {
      setShowConfirmModal(true);
    }
  };

  /**
   * Confirms the dataDriven toggle after user acknowledgement.
   */
  const confirmDataDrivenChange = (): void => {
    /* istanbul ignore else */
    if (groupingFactor) {
      if (isGroupingFactorDataDriven(groupingFactor)) {
        const newGroupingFactor: GroupingFactorWithGroups = {
          dataDriven: false,
          description: groupingFactor.description,
          fromHierarchyId: groupingFactor.fromHierarchyId,
          groups: [],
          id: groupingFactor.id,
          label: groupingFactor.label,
          name: groupingFactor.name,
        };
        dispatch({
          operation: PATCH_ARS_STATE,
          dataPath,
          value: newGroupingFactor,
        });
      } else {
        const newGroupingFactor: GroupingFactorDataDriven = {
          dataDriven: true,
          dataValues: [],
          description: groupingFactor.description,
          fromHierarchyId: groupingFactor.fromHierarchyId,
          groupingDataset: "",
          groupingVariable: "",
          id: groupingFactor.id,
          label: groupingFactor.label,
          name: groupingFactor.name,
        };
        dispatch({
          operation: PATCH_ARS_STATE,
          dataPath,
          value: newGroupingFactor,
        });
      }
    }
    setShowConfirmModal(false);
  };

  /**
   * Handles adding a new group to the groups array.
   */
  const handleAddGroup = (): void => {
    /* istanbul ignore else */
    if (!disabled && isGroupingFactorWithGroups(groupingFactor)) {
      const newGroup: GroupWithCondition = createNewGroup(
        `Group_${groups.length + 1}`,
        groups.length,
      );

      dispatch({
        operation: PATCH_ARS_STATE,
        dataPath: `${dataPath}/groups`,
        value: [...groupingFactor.groups, newGroup],
      });
    }
  };

  /**
   * Handles reordering of the groups array after a tab has been dragged.
   *
   * @param newGroups - The groups in their new order, with order values re-applied.
   */
  const handleReorderGroups = (newGroups: Group[]): void => {
    /* istanbul ignore else -- reordering is disabled unless groups are present */
    if (!disabled && isGroupingFactorWithGroups(groupingFactor)) {
      const groups: Group[] = newGroups.map((group, index) => ({ ...group, order: index }));

      dispatch({
        operation: PATCH_ARS_STATE,
        dataPath: `${dataPath}/groups`,
        value: groups,
      });
    }
  };

  return (
    <>
      <div className="border rounded p-3 mb-3 position-relative">
        {groupingFactor && (
          <div className="position-absolute bottom-0 end-0 m-1">
            <Form.Text>
              <small>Id: {dataPath.split("/").pop()}</small>
            </Form.Text>
          </div>
        )}
        {canRename && (
          <div className="position-absolute top-0 end-0 m-2">
            <RemoveArsItemButton
              dataPath={dataPath}
              disabled={disabled}
            />
          </div>
        )}
        <Tab.Container
          activeKey={activeTab}
          onSelect={(k) => {
            /* istanbul ignore else -- Disabled tabs and null selections are not reachable from the UI */
            if (k) {
              setActiveTab(k);
            }
          }}
        >
          <Nav
            variant="tabs"
            className="mb-3"
          >
            <Nav.Item>
              <Nav.Link eventKey="named-object">Properties</Nav.Link>
            </Nav.Item>
            {dataDriven ? (
              <Nav.Item>
                <Nav.Link eventKey="dataset-variable">Dataset/Variable</Nav.Link>
              </Nav.Item>
            ) : (
              <ReorderableNavList
                list={groups}
                setList={handleReorderGroups}
                getEventKey={(_group, index) => `group-${index}`}
                disabled={disabled}
              />
            )}
          </Nav>
          <Tab.Content>
            <Tab.Pane eventKey="named-object">
              <NamedObjectControl
                dataPath={dataPath}
                canRename={canRename}
                disabled={disabled}
              />
              <Form.Group
                controlId={`${dataPath.replace(/\//g, "-")}-data-driven`}
                as={Row}
                className="my-1"
                title="Data driven"
              >
                <Form.Label
                  column
                  md={2}
                >
                  Data driven
                </Form.Label>
                <Col
                  md={10}
                  className="d-flex align-items-center justify-content-start"
                >
                  <Form.Check
                    type="switch"
                    checked={dataDriven}
                    disabled={disabled}
                    onChange={handleDataDrivenChange}
                  />
                </Col>
              </Form.Group>
              <Form.Group
                controlId={`${dataPath.replace(/\//g, "-")}-add-group`}
                className="mt-3"
              >
                {!disabled && (
                  <AsyncButton
                    id={`${dataPath.replace(/\//g, "-")}-add-group-btn`}
                    onClick={handleAddGroup}
                    className="d-flex align-items-center btn-sm btn-success"
                    disabled={dataDriven}
                  >
                    <span className="me-2">Add group</span>
                    <PlusCircle />
                  </AsyncButton>
                )}
              </Form.Group>
            </Tab.Pane>
            {dataDriven ? (
              <Tab.Pane eventKey="dataset-variable">
                <ArsVariable
                  datasetDataPath={`${dataPath}/groupingDataset`}
                  variableDataPath={`${dataPath}/groupingVariable`}
                  id={`${dataPath.replace(/\//g, "-")}-grouping-variable`}
                  label="Grouping variable"
                  labelWidth={2}
                  placeHolder="Select dataset.variable"
                  disabled={disabled}
                />
                <ArsVariable
                  datasetDataPath={`${dataPath}/groupingDataset`}
                  variableDataPath={`${dataPath}/orderVariable`}
                  id={`${dataPath.replace(/\//g, "-")}-order-variable`}
                  canChangeDataset={false}
                  disabled={disabled || !groupingFactor?.groupingDataset}
                  label="Order variable"
                  labelWidth={2}
                  placeHolder="Select dataset.variable"
                />
                <ArsVariable
                  datasetDataPath={`${dataPath}/groupingDataset`}
                  variableDataPath={`${dataPath}/unitVariable`}
                  id={`${dataPath.replace(/\//g, "-")}-unit-variable`}
                  canChangeDataset={false}
                  disabled={disabled || !groupingFactor?.groupingDataset}
                  label="Unit variable"
                  labelWidth={2}
                  placeHolder="Select dataset.variable"
                />
                <ArsVariable
                  datasetDataPath={`${dataPath}/groupingDataset`}
                  variableDataPath={`${dataPath}/otherVariable`}
                  id={`${dataPath.replace(/\//g, "-")}-other-variable`}
                  canChangeDataset={false}
                  disabled={disabled || !groupingFactor?.groupingDataset}
                  label="Other variable"
                  labelWidth={2}
                  placeHolder="Select dataset.variable"
                />
                <ArsText
                  dataPath={`${dataPath}/dataValues`}
                  id={`${dataPath.replace(/\//g, "-")}-data-values`}
                  debounceMilliseconds={2000}
                  disabled={disabled}
                  isArray
                  label="Data values"
                  labelWidth={2}
                  rows={5}
                  type="textarea"
                />
              </Tab.Pane>
            ) : (
              groups.map((group, index) => (
                <Tab.Pane
                  key={group.id}
                  eventKey={`group-${index}`}
                >
                  <GroupControl
                    dataPath={`${dataPath}/groups/${index}`}
                    disabled={disabled}
                    siblingCount={groups.length}
                  />
                </Tab.Pane>
              ))
            )}
          </Tab.Content>
        </Tab.Container>
      </div>
      <Modal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm change</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Changing the Data driven setting will result in loss of any existing changes.</p>
          <p>Are you sure you want to proceed?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConfirmModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={confirmDataDrivenChange}
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

GroupingFactorControl.displayName = "GroupingFactorControl";
